import React from 'react';
import { trackEvent } from '../services/analytics';
import { manhuaCover } from '../services/api';

const KIND_LABEL = {
  manhwa: { label: '🇰🇷 MANHWA', cls: 'bg-rose-600' },
  manhua: { label: '🇨🇳 MANHUA', cls: 'bg-amber-600' },
  manga: { label: '🇯🇵 MANGA', cls: 'bg-indigo-600' },
};

const STATUS_LABEL = {
  ongoing: 'Ongoing',
  completed: 'Completed',
  hiatus: 'Hiatus',
  cancelled: 'Cancelled',
};

const truncate = (text, max = 180) => {
  if (!text) return '';
  return text.length <= max ? text : `${text.slice(0, max).trim()}…`;
};

const ManhuaCard = ({ manhua, onClick }) => {
  const kind = KIND_LABEL[manhua.content_kind] || KIND_LABEL.manga;
  const cover = manhuaCover(manhua.poster_path, 512); // proxied + downscaled thumbnail
  const rating = manhua.vote_average || 0;
  // Availability badge — only shown when we've actually chapter-checked the title
  // (read_status set by the chapter-sync job). 'unknown'/absent => no badge, so we
  // never promise what we haven't verified.
  const readStatus = manhua.read_status;

  const handleClick = () => {
    trackEvent('Manhua Result Clicked', {
      manhua_id: manhua.id,
      title: manhua.title,
      score: manhua.similarity_score,
    });
    onClick && onClick(manhua);
  };

  return (
    <div
      onClick={handleClick}
      className="group bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden cursor-pointer hover:border-rose-500/60 hover:shadow-rose-500/20 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
    >
      {/* Vertical cover */}
      <div className="relative aspect-[2/3] bg-zinc-800 overflow-hidden">
        {cover ? (
          <img
            src={cover}
            alt={`${manhua.title} cover`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-500 text-xs">
            No cover
          </div>
        )}

        {/* Gradient overlay for legibility */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none" />

        {/* Kind badge */}
        <div className="absolute top-2 left-2">
          <span className={`inline-block px-2 py-1 text-[10px] font-bold rounded text-white tracking-wider ${kind.cls}`}>
            {kind.label}
          </span>
        </div>

        {/* Rating badge */}
        {rating > 0 && (
          <div className="absolute top-2 right-2">
            <span className="inline-block px-2 py-1 text-[11px] font-semibold bg-yellow-400/90 text-zinc-900 rounded-full">
              ⭐ {rating.toFixed(1)}
            </span>
          </div>
        )}

        {/* Availability badge (bottom-right) */}
        {readStatus === 'readable' && (
          <div className="absolute bottom-2 right-2">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold bg-emerald-500 text-white rounded-full shadow">
              <span className="w-1.5 h-1.5 bg-white rounded-full" /> Read
            </span>
          </div>
        )}
        {readStatus === 'external' && (
          <div className="absolute bottom-2 right-2">
            <span className="inline-block px-2 py-0.5 text-[10px] font-semibold bg-black/70 text-zinc-200 rounded-full">
              Official ↗
            </span>
          </div>
        )}

        {/* Status pill */}
        {manhua.status && (
          <div className="absolute bottom-2 left-2">
            <span className="inline-block px-2 py-0.5 text-[10px] font-medium bg-black/60 text-zinc-100 rounded">
              {STATUS_LABEL[manhua.status] || manhua.status}
              {manhua.year && <span className="text-zinc-400"> · {manhua.year}</span>}
            </span>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="p-3 text-zinc-100">
        <h3 className="font-semibold text-base leading-snug line-clamp-2 mb-1 group-hover:text-rose-400 transition-colors">
          {manhua.title}
        </h3>

        {/* Genre chips */}
        {manhua.genres?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {manhua.genres.slice(0, 3).map((g) => (
              <span key={g} className="text-[10px] px-2 py-0.5 bg-rose-500/15 text-rose-300 rounded-full border border-rose-500/30">
                {g}
              </span>
            ))}
          </div>
        )}

        {manhua.overview && (
          <p className="text-xs text-zinc-400 line-clamp-3 leading-relaxed">
            {truncate(manhua.overview, 160)}
          </p>
        )}

        {manhua.authors?.length > 0 && (
          <p className="text-[11px] text-zinc-500 mt-2">
            <span className="text-zinc-400">by</span> {manhua.authors.slice(0, 2).join(', ')}
          </p>
        )}
      </div>
    </div>
  );
};

export default ManhuaCard;
